import React, { useCallback } from 'react'
import { Map, List } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { getBoundActions } from '../../actions/index'
import {
  URL_ACCOUNT_CHANGE_PLAN,
  URL_PLAN_SELECTION_PLANS,
  URL_ACCOUNT,
  URL_HOME,
} from '../../services/url/constants'
import { getEmailCustomerServiceUrl } from '../../services/url/index'
import { getPrimary } from '../../services/languages/index'
import { CHECKOUT_ORDER_ERROR_TYPE_PAYMENT } from '../../services/checkout/index'
import {
  PLAN_NOT_FOUND,
  PLAN_DISABLED,
  PLAN_CHANGE_NOT_ALLOWED,
  FREE_TRIAL_NOT_ALLOWED,
  USER_REQUIRED,
  SUBSCRIPTION_NOT_FOUND,
  INACTIVE_SUBSCRIPTION,
  USER_ALREADY_HAS_PLAN,
  SUBSCRIPTION_PAUSED,
  ACTIVE_SUBSCRIPTION,
  ACTIVE_ENTITLEMENTS,
  RULES_FAILED,
  SUBSCRIPTION_SUSPENDED,
} from '../../services/plans/index'
import {
  BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN,
  BILLING_PLAN_GRID_LOCATION_CHECKOUT,
} from '../../services/plans/utils'
import {
  clearSessionStorage,
  SESSION_STORAGE_GAIA_PLAN_SELECTION,
} from '../../services/local-preferences/index'
import {
  PLAN_ELIGIBILITY_RETURN_BUTTON_CLICKED_GA4_EVENT,
  PLAN_ELIGIBILITY_RESUME_SUBSCRIPTION_CLICKED_GA4_EVENT,
} from '../../services/event-tracking/index'
import { hasActivePause } from '../../services/user-account/index'
import Button, { TYPES } from '../Button/index'
import { H2, HEADING_TYPES } from '../Heading/index'
import Link, { TARGET_BLANK } from '../Link/index'

/** Redux-driven UI branch for subscription resume inside plan-access messaging */
export const PLAN_ACCESS_RESUME_UI_STATE = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
  PAYMENT_ERROR: 'paymentError',
} as const

export type PlanAccessResumeUiState =
  (typeof PLAN_ACCESS_RESUME_UI_STATE)[keyof typeof PLAN_ACCESS_RESUME_UI_STATE]

function getManageSubscriptionData (userAccount: Map<string, unknown>): Map<string, unknown> {
  if (!Map.isMap(userAccount)) {
    return Map()
  }
  const data = userAccount.getIn(['manageSubscription', 'data'])
  return Map.isMap(data) ? data : Map()
}

function getResumeUiState (manageData: Map<string, unknown>): PlanAccessResumeUiState {
  if (!Map.isMap(manageData) || manageData.isEmpty()) {
    return PLAN_ACCESS_RESUME_UI_STATE.IDLE
  }

  if (manageData.get('success') === true) {
    return PLAN_ACCESS_RESUME_UI_STATE.SUCCESS
  }

  if (manageData.get('accountResumeProcessing') === true) {
    return PLAN_ACCESS_RESUME_UI_STATE.PROCESSING
  }

  const errorsRaw = manageData.get('errors')
  const errorsList = (List.isList(errorsRaw) ? errorsRaw : List()) as List<unknown>
  const hasErrors = errorsList.size > 0
  const apiErrorCode = manageData.get('errorCode', '') as string

  if (hasErrors && apiErrorCode === CHECKOUT_ORDER_ERROR_TYPE_PAYMENT) {
    return PLAN_ACCESS_RESUME_UI_STATE.PAYMENT_ERROR
  }

  if (hasErrors) {
    return PLAN_ACCESS_RESUME_UI_STATE.ERROR
  }

  return PLAN_ACCESS_RESUME_UI_STATE.IDLE
}

interface PlanAccessMessagesProps {
  staticText: Map<string, string>
  eligibility: Map<string, unknown>
  page: string
  userAccount: Map<string, unknown>
  user: Map<string, unknown>
  updateUserAccountResume: (payload?: { skipPostSuccessNavigation?: boolean }) => void
  resetUserAccountManageSubscriptionData: () => void
  dismissModal: (clickedOverlay?: unknown) => void
  deletePlansSelection: () => void
  setEventDefault: (event: Map<string, unknown>) => void
}

interface PlanAccessErrorMessageProps {
  errorCode: string
  staticText: Map<string, string>
  accountOnlyOverride: boolean
}

interface PlanAccessMessageTitleProps {
  errorCode: string
  staticText: Map<string, string>
  accountOnlyOverride: boolean
}

interface PlanAccessMessageCtaProps {
  staticText: Map<string, string>
  errorCode: string
  page: string
  accountOnlyOverride: boolean
  deletePlansSelection: () => void
  setEventDefault: (event: Map<string, unknown>) => void
}

interface SubscriptionPausedResumeProps {
  staticText: Map<string, string>
  userAccount: Map<string, unknown>
  user: Map<string, unknown>
  page: string
  updateUserAccountResume: (payload?: { skipPostSuccessNavigation?: boolean }) => void
  resetUserAccountManageSubscriptionData: () => void
  dismissModal: (clickedOverlay?: unknown) => void
  setEventDefault: (event: Map<string, unknown>) => void
}

function PlanAccessErrorMessage (props: PlanAccessErrorMessageProps) {
  const { errorCode, staticText, accountOnlyOverride } = props
  let message = ''

  // if account only override, show unavailable only message
  if (accountOnlyOverride) {
    message = staticText.get('unavailableOnly')
    return <p className="plan-access-messages__message">{message}</p>
  }

  if (!errorCode) {
    message = staticText.get('planNotAvailable')
  }

  switch (errorCode) {
    case PLAN_NOT_FOUND:
      message = staticText.get('planNotFound')
      break
    case PLAN_DISABLED:
    case FREE_TRIAL_NOT_ALLOWED:
    case PLAN_CHANGE_NOT_ALLOWED:
      message = staticText.get('planNotAvailable')
      break
    case RULES_FAILED:
      message = staticText.get('planUnavailable')
      break
    case USER_REQUIRED:
      message = staticText.get('userRequired')
      break
    case ACTIVE_SUBSCRIPTION:
    case ACTIVE_ENTITLEMENTS:
      message = staticText.get('activeSubscription')
      break
    case SUBSCRIPTION_PAUSED:
      message = staticText.get('subscriptionPaused')
      break
    case SUBSCRIPTION_NOT_FOUND:
    case INACTIVE_SUBSCRIPTION:
      message = staticText.get('subscriptionNotFound')
      break
    case USER_ALREADY_HAS_PLAN:
      message = staticText.get('userAlreadyHasPlan')
      break
    case SUBSCRIPTION_SUSPENDED:
      message = staticText.get('paymentProcessingProblem')
      break
    default:
      message = staticText.get('planNotAvailable')
  }

  return <p className="plan-access-messages__message">{message}</p>
}

function PlanAccessMessageTitle (props: PlanAccessMessageTitleProps) {
  const { errorCode, staticText, accountOnlyOverride } = props
  let title = ''

  switch (errorCode) {
    case PLAN_NOT_FOUND:
    case PLAN_DISABLED:
    case PLAN_CHANGE_NOT_ALLOWED:
    case FREE_TRIAL_NOT_ALLOWED:
    case RULES_FAILED:
      title = staticText.get('planUnavailableTitle')
      break
    case USER_REQUIRED:
      title = staticText.get('accountIssueTitle')
      break
    case ACTIVE_SUBSCRIPTION:
    case ACTIVE_ENTITLEMENTS:
    case SUBSCRIPTION_PAUSED:
    case SUBSCRIPTION_NOT_FOUND:
    case INACTIVE_SUBSCRIPTION:
    case USER_ALREADY_HAS_PLAN:
      title = staticText.get('subscriptionIssueTitle')
      break
    case SUBSCRIPTION_SUSPENDED:
      title = staticText.get('paymentIssueTitle')
      break
    default:
      title = staticText.get('planUnavailableTitle')
  }

  // if account only override, show plan unavailable title
  if (accountOnlyOverride) {
    title = staticText.get('planUnavailableTitle')
  }

  return <H2 as={HEADING_TYPES.H4} className="plan-access-messages__title">{title}</H2>
}

function getPlanSelectionUrl (page: string, errorCode: string) {
  let url = URL_ACCOUNT_CHANGE_PLAN
  if (page === BILLING_PLAN_GRID_LOCATION_CHECKOUT) {
    url = URL_PLAN_SELECTION_PLANS
  }

  if (errorCode === SUBSCRIPTION_NOT_FOUND || errorCode === INACTIVE_SUBSCRIPTION) {
    url = URL_PLAN_SELECTION_PLANS
  }

  return url
}

function PlanAccessMessageCta (props: PlanAccessMessageCtaProps) {
  const {
    staticText,
    errorCode,
    page,
    deletePlansSelection,
    accountOnlyOverride,
    setEventDefault,
  } = props

  function handleDeletePlansSelection () {
    deletePlansSelection()
    // clear the gaia plan selection from session storage
    clearSessionStorage(SESSION_STORAGE_GAIA_PLAN_SELECTION)
  }

  const buttons = []
  const returnToPlanSelectionButton = (
    <Button
      key="returnToPlanSelectionButton"
      type={TYPES.PRIMARY}
      url={getPlanSelectionUrl(page, errorCode)}
      onClick={() => {
        setEventDefault(PLAN_ELIGIBILITY_RETURN_BUTTON_CLICKED_GA4_EVENT)
        handleDeletePlansSelection()
      }}
    >
      {staticText.get('returnToPlanSelection')}
    </Button>
  )
  const myAccountButton = (
    <Button
      key="myAccountButton"
      type={TYPES.PRIMARY}
      url={URL_ACCOUNT}
      onClick={handleDeletePlansSelection}
    >
      {staticText.get('myAccount')}
    </Button>
  )
  const gaiaHomeButton = (
    <Button
      key="gaiaHomeButton"
      type={TYPES.PRIMARY}
      url={URL_HOME}
      onClick={handleDeletePlansSelection}
    >
      {staticText.get('gaiaHome')}
    </Button>
  )

  // if account only override, only show my account button
  if (accountOnlyOverride) {
    buttons.push(myAccountButton)
    return buttons
  }

  switch (errorCode) {
    case PLAN_NOT_FOUND:
    case PLAN_DISABLED:
    case PLAN_CHANGE_NOT_ALLOWED:
    case FREE_TRIAL_NOT_ALLOWED:
      buttons.push(returnToPlanSelectionButton)
      break
    case USER_REQUIRED:
    case SUBSCRIPTION_SUSPENDED:
      buttons.push(myAccountButton)
      break
    case SUBSCRIPTION_NOT_FOUND:
    case INACTIVE_SUBSCRIPTION:
      buttons.push(myAccountButton)
      buttons.push(returnToPlanSelectionButton)
      break
    case USER_ALREADY_HAS_PLAN:
      buttons.push(returnToPlanSelectionButton)
      buttons.push(myAccountButton)
      break
    case ACTIVE_SUBSCRIPTION:
    case ACTIVE_ENTITLEMENTS:
      buttons.push(myAccountButton)
      buttons.push(gaiaHomeButton)
      break
    case RULES_FAILED:
      buttons.push(returnToPlanSelectionButton)
      break
    default:
      buttons.push(returnToPlanSelectionButton)
      break
  }

  return buttons
}

function SubscriptionPausedResume (props: SubscriptionPausedResumeProps) {
  const {
    staticText,
    userAccount,
    user,
    page,
    updateUserAccountResume,
    resetUserAccountManageSubscriptionData,
    dismissModal,
    setEventDefault,
  } = props

  const manageData = getManageSubscriptionData(userAccount)
  const resumeState = getResumeUiState(manageData)
  const userLanguage = Map.isMap(user) ? getPrimary(user.getIn(['data', 'language'])) : undefined
  const supportUrl = getEmailCustomerServiceUrl(userLanguage)

  const onResume = useCallback(() => {
    updateUserAccountResume({ skipPostSuccessNavigation: true })
  }, [updateUserAccountResume])

  const onResumeSubscriptionClick = useCallback(() => {
    setEventDefault(PLAN_ELIGIBILITY_RESUME_SUBSCRIPTION_CLICKED_GA4_EVENT)
    updateUserAccountResume({ skipPostSuccessNavigation: true })
  }, [setEventDefault, updateUserAccountResume])

  const onContinueSuccess = useCallback(() => {
    dismissModal(false)
    resetUserAccountManageSubscriptionData()
  }, [dismissModal, resetUserAccountManageSubscriptionData])

  const myAccountButton = (
    <Button
      key="myAccountButton"
      type={TYPES.SECONDARY}
      url={URL_ACCOUNT}
    >
      {staticText.get('myAccount')}
    </Button>
  )

  if (resumeState === PLAN_ACCESS_RESUME_UI_STATE.SUCCESS) {
    return (
      <div className="plan-access-messages plan-access-messages--subscription-paused">
        <H2 as={HEADING_TYPES.H4} className="plan-access-messages__title">
          {staticText.get('resumeSuccessTitle')}
        </H2>
        <p className="plan-access-messages__message">{staticText.get('resumeSuccessBody')}</p>
        <div className="plan-access-messages__actions">
          <Button type={TYPES.PRIMARY} onClick={onContinueSuccess}>
            {staticText.get('resumeContinue')}
          </Button>
        </div>
      </div>
    )
  }

  if (resumeState === PLAN_ACCESS_RESUME_UI_STATE.PAYMENT_ERROR) {
    return (
      <div className="plan-access-messages plan-access-messages--subscription-paused plan-access-messages--error">
        <H2 as={HEADING_TYPES.H4} className="plan-access-messages__title">
          {staticText.get('resumePaymentErrorTitle')}
        </H2>
        <div className="plan-access-messages__message plan-access-messages__message--block">
          <span className="plan-access-messages__emphasis">
            {staticText.get('resumePaymentErrorLead')}
          </span>
          {staticText.get('resumePaymentErrorBody1')}
          <Link
            className="plan-access-messages__link"
            to={supportUrl}
            directLink
            target={TARGET_BLANK}
          >
            {staticText.get('resumeErrorSupportLink')}
          </Link>
          {staticText.get('resumePaymentErrorBody2')}
        </div>
        <div className="plan-access-messages__actions">
          <Button type={TYPES.PRIMARY} onClick={onResume}>
            {staticText.get('resumeUpdatePaymentRetry')}
          </Button>
          {myAccountButton}
        </div>
      </div>
    )
  }

  if (resumeState === PLAN_ACCESS_RESUME_UI_STATE.ERROR) {
    return (
      <div className="plan-access-messages plan-access-messages--subscription-paused plan-access-messages--error">
        <H2 as={HEADING_TYPES.H4} className="plan-access-messages__title">
          {staticText.get('resumeErrorTitle')}
        </H2>
        <div className="plan-access-messages__message plan-access-messages__message--block">
          {staticText.get('resumeErrorBody1')}
          <Link
            className="plan-access-messages__link"
            to={supportUrl}
            directLink
            target={TARGET_BLANK}
          >
            {staticText.get('resumeErrorSupportLink')}
          </Link>
          {staticText.get('resumeErrorBody2')}
        </div>
        <div className="plan-access-messages__actions">
          <Button type={TYPES.PRIMARY} onClick={onResume}>
            {staticText.get('resumeErrorRetry')}
          </Button>
          {myAccountButton}
        </div>
      </div>
    )
  }

  if (resumeState === PLAN_ACCESS_RESUME_UI_STATE.PROCESSING) {
    return (
      <div className="plan-access-messages plan-access-messages--subscription-paused">
        <PlanAccessMessageTitle
          errorCode={SUBSCRIPTION_PAUSED}
          staticText={staticText} accountOnlyOverride={false} />
        <PlanAccessErrorMessage
          errorCode={SUBSCRIPTION_PAUSED}
          staticText={staticText}
          accountOnlyOverride={false}
        />
        <div className="plan-access-messages__actions">
          <Button type={TYPES.PRIMARY} disabled>
            {staticText.get('resumeProcessing')}
          </Button>
          {page === BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN ? (
            <Button type={TYPES.SECONDARY} url={URL_ACCOUNT}>
              {staticText.get('myAccount')}
            </Button>
          ) : (
            myAccountButton
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="plan-access-messages plan-access-messages--subscription-paused">
      <PlanAccessMessageTitle
        errorCode={SUBSCRIPTION_PAUSED}
        staticText={staticText}
        accountOnlyOverride={false}
      />
      <PlanAccessErrorMessage
        errorCode={SUBSCRIPTION_PAUSED}
        staticText={staticText}
        accountOnlyOverride={false}
      />
      <div className="plan-access-messages__actions">
        <Button type={TYPES.PRIMARY} onClick={onResumeSubscriptionClick}>
          {staticText.get('resumeSubscription')}
        </Button>
        {page === BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN ? (
          <Button type={TYPES.SECONDARY} url={URL_ACCOUNT}>
            {staticText.get('myAccount')}
          </Button>
        ) : (
          myAccountButton
        )}
      </div>
    </div>
  )
}

function PlanAccessMessages (props: PlanAccessMessagesProps) {
  const {
    staticText = Map(),
    eligibility = Map(),
    page,
    userAccount = Map(),
    user = Map(),
    updateUserAccountResume,
    resetUserAccountManageSubscriptionData,
    dismissModal,
    setEventDefault,
    deletePlansSelection,
  } = props
  const errorCode = eligibility.get('errorCode') as string
  const isPaused = hasActivePause(userAccount)
  const accountOnlyOverride = isPaused && errorCode !== SUBSCRIPTION_PAUSED

  if (!errorCode) {
    return null
  }

  if (errorCode === SUBSCRIPTION_PAUSED) {
    return (
      <SubscriptionPausedResume
        staticText={staticText}
        userAccount={userAccount}
        user={user}
        page={page}
        updateUserAccountResume={updateUserAccountResume}
        resetUserAccountManageSubscriptionData={resetUserAccountManageSubscriptionData}
        dismissModal={dismissModal}
        setEventDefault={setEventDefault}
      />
    )
  }

  return (
    <div className="plan-access-messages">
      <PlanAccessMessageTitle
        errorCode={errorCode}
        staticText={staticText}
        accountOnlyOverride={accountOnlyOverride}
      />
      <PlanAccessErrorMessage
        errorCode={errorCode}
        staticText={staticText}
        accountOnlyOverride={accountOnlyOverride}
      />
      <div className="plan-access-messages__actions">
        <PlanAccessMessageCta
          errorCode={errorCode}
          staticText={staticText}
          page={page}
          deletePlansSelection={deletePlansSelection}
          accountOnlyOverride={accountOnlyOverride}
          setEventDefault={setEventDefault}
        />
      </div>
    </div>
  )
}

export default compose(
  connectRedux(
    (state: {
      staticText: Map<string, unknown>
      userAccount: Map<string, unknown>
      user: Map<string, unknown>
    }) => ({
      staticText: (state.staticText.getIn(
        ['data', 'planAccessMessages', 'data'],
        Map(),
      )
      || Map()) as Map<string, string>,
      userAccount: state.userAccount,
      user: state.user,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        updateUserAccountResume: actions.userAccount.updateUserAccountResume,
        resetUserAccountManageSubscriptionData:
          actions.userAccount.resetUserAccountManageSubscriptionData,
        dismissModal: actions.dialog.dismissModal,
        deletePlansSelection: actions.plans.deletePlansSelection,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(PlanAccessMessages as React.ComponentType<unknown>)
